import { reducerWithInitialState } from 'typescript-fsa-reducers';
import { RuleValidity, RuleStatus } from '@cbridge-tools/rules-data-grid';
import { getStatusFilterState } from '@cbridge-tools/rules-data-grid-filter';
import { makeNewRowAtIndex, refreshIndexes, RuleWarningType } from '@cbridge-packages/rule';
import { RulesManagementAction } from './rulesManagement.action';
import { rulesManagementInitialState } from './rulesManagement.constant';
import { FilterMode, IRulesManagementStore, IPermittedRuleRowModel } from './rulesManagement.interface';

export const rulesManagementReducer = reducerWithInitialState(rulesManagementInitialState)
  .case(RulesManagementAction.updateState, (state, payload) => {
    const {
      errorsCount, unusedCount, uselessCount, filterByStatus, rules, invalidRules,
    } = payload;

    const validityFilter = state.filter.validity;

    return {
      ...state,
      count: rules?.length ?? 0,
      rules,
      filter: {
        ...state.filter,
        status: filterByStatus ?? state.filter.status,
        validity: {
          ...validityFilter,
          [RuleValidity.Error]: { ...validityFilter?.[RuleValidity.Error], count: errorsCount },
          [RuleValidity.Unused]: { ...validityFilter?.[RuleValidity.Unused], count: unusedCount },
          [RuleValidity.Useless]: { ...validityFilter?.[RuleValidity.Useless], count: uselessCount },
        },
      },
      invalidRules: invalidRules ?? state.invalidRules,
    };
  })
  .case(RulesManagementAction.toggleStatusFilter, (state, status) => {
    const currentStatusFilter = state.filter.status[status];

    return {
      ...state,
      filter: {
        ...state.filter,
        status: {
          ...state.filter.status,
          [status]: { ...currentStatusFilter, isActive: !currentStatusFilter?.isActive },
        },
      },
    };
  })
  .case(RulesManagementAction.beginEditing, (state, { emptyRowModel }) => {
    if (!state.rules?.length && emptyRowModel) { // Need to add first empty row
      const defaultRule = makeNewRowAtIndex(emptyRowModel, 0);

      return {
        ...state,
        count: 1,
        filter: {
          ...state.filter,
          status: getStatusFilterState(1),
        },
        rules: [defaultRule],
        invalidRules: new Set([defaultRule.id]),
        editMode: true,
      };
    }

    return {
      ...state,
      editMode: true,
    };
  })
  .case(RulesManagementAction.endEditing, (state, payload) => {
    if (!payload) {
      return {
        ...state,
        filter: {
          ...state.filter,
          status: getStatusFilterState(),
        },
        editMode: false,
        invalidRules: new Set(),
      };
    }

    const { errorsCount, unusedCount, uselessCount, rules } = payload;

    return {
      ...state,
      count: rules?.length ?? 0,
      rules,
      filter: {
        ...state.filter,
        status: getStatusFilterState(),
        validity: {
          ...state.filter.validity,
          [RuleValidity.Error]: { ...state.filter.validity?.[RuleValidity.Error], count: errorsCount },
          [RuleValidity.Unused]: { ...state.filter.validity?.[RuleValidity.Unused], count: unusedCount },
          [RuleValidity.Useless]: { ...state.filter.validity?.[RuleValidity.Useless], count: uselessCount },
        },
      },
      editMode: false,
      // Cancel rebuilds the rows from the persisted store — stale invalid flags must not
      // survive into the next editing session (they would keep Save disabled forever).
      invalidRules: new Set(),
    };
  })
  .case(RulesManagementAction.addRule, (state, payload) => {
    const { index, emptyRowModel } = payload;
    const rules = [...state.rules];
    const invalidRules = new Set(state.invalidRules);

    if (emptyRowModel) {
      const defaultRule = makeNewRowAtIndex(emptyRowModel, index);
      rules.splice(index, 0, defaultRule);
      invalidRules.add(defaultRule.id);
    } else {
      const clonedRule = makeNewRowAtIndex(rules[index - 1], index);
      rules.splice(index, 0, clonedRule);
      if (invalidRules.has(rules[index - 1].id)) {
        invalidRules.add(clonedRule.id);
      }
    }
    refreshIndexes(rules, index + 1, rules.length);

    return {
      ...state,
      rules,
      count: state.count + 1,
      filter: {
        ...state.filter,
        status: {
          ...state.filter.status,
          [RuleStatus.Added]: { count: state.filter.status[RuleStatus.Added].count + 1 },
        },
      },
      invalidRules,
    };
  })
  .case(RulesManagementAction.removeRule, (state, payload) => {
    const { index } = payload;

    const rules = [...state.rules];
    const currentRule = rules[index];
    currentRule.propsBeforeDelete = {};

    const invalidRules = new Set(state.invalidRules);
    if (invalidRules.has(currentRule.id)) {
      currentRule.propsBeforeDelete.invalid = true;
    }
    invalidRules.delete(currentRule.id);

    const newStatus = { ...state.filter.status };

    if (currentRule.status === RuleStatus.Added) {
      rules.splice(index, 1);
      refreshIndexes(rules, index, rules.length);
      newStatus[RuleStatus.Added] = { count: state.filter.status[RuleStatus.Added].count - 1 };
    } else {
      if (currentRule.status === RuleStatus.Updated) {
        newStatus[RuleStatus.Updated] = { count: state.filter.status[RuleStatus.Updated].count - 1 };
        currentRule.propsBeforeDelete.status = RuleStatus.Updated;
      }
      currentRule.status = RuleStatus.Deleted;
      newStatus[RuleStatus.Deleted] = { count: state.filter.status[RuleStatus.Deleted].count + 1 };
    }

    return {
      ...state,
      rules,
      count: state.count - 1,
      filter: {
        ...state.filter,
        status: newStatus,
      },
      invalidRules,
    };
  })
  .case(RulesManagementAction.restoreRule, (state, payload) => {
    const { index } = payload;

    const rules = [...state.rules];
    const invalidRules = new Set(state.invalidRules);
    const currentRule = rules[index];

    const newStatus = {
      ...state.filter.status,
      [RuleStatus.Deleted]: { count: state.filter.status[RuleStatus.Deleted].count - 1 },
    };

    if (currentRule.propsBeforeDelete?.status === RuleStatus.Updated) {
      currentRule.status = RuleStatus.Updated;
      newStatus[RuleStatus.Updated] = { count: state.filter.status[RuleStatus.Updated].count + 1 };
    } else {
      delete currentRule.status;
    }

    if (currentRule.propsBeforeDelete?.invalid) {
      invalidRules.add(currentRule.id);
    }
    delete currentRule.propsBeforeDelete;

    return {
      ...state,
      rules,
      count: state.count + 1,
      filter: {
        ...state.filter,
        status: newStatus,
      },
      invalidRules,
    };
  })
  .case(RulesManagementAction.updateRule, (state, payload) => {
    const { updatedRule, isEqualsToOriginal } = payload;

    const rules = [...state.rules];

    const { index, status } = updatedRule;
    let updatedCountDiff = 0;

    rules[index] = updatedRule;

    switch (status) {
      case RuleStatus.Added:
        break; //do nothing
      case RuleStatus.Updated:
        if (isEqualsToOriginal(updatedRule)) {
          delete rules[index].status; //values were reverted to default
          updatedCountDiff = -1;
        }
        break;
      default:
        rules[index].status = RuleStatus.Updated;
        updatedCountDiff = 1;
    }

    return {
      ...state,
      rules,
      filter: {
        ...state.filter,
        status: updatedCountDiff !== 0
          ? {
            ...state.filter.status,
            [RuleStatus.Updated]: { count: state.filter.status[RuleStatus.Updated].count + updatedCountDiff },
          }
          : state.filter.status,
      },
    };
  })
  .case(RulesManagementAction.reorderRule, (state, payload) => {
    const { targetIndex, oldIndex, isEqualsToOriginal } = payload;

    const rules = [...state.rules];
    const targetRow = rules.splice(oldIndex, 1)[0];
    rules.splice(targetIndex, 0, targetRow);

    const min = Math.min(targetIndex, oldIndex);
    const max = Math.max(targetIndex, oldIndex);
    refreshIndexes(rules, min, max + 1);

    let updatedCountDiff = 0;

    if (isEqualsToOriginal(rules[targetIndex])) {
      delete rules[targetIndex].status; //row returned to the initial position
      updatedCountDiff = -1;
    } else if (!rules[targetIndex].status) {
      rules[targetIndex].status = RuleStatus.Updated;
      updatedCountDiff = 1;
    }

    return {
      ...state,
      rules,
      filter: {
        ...state.filter,
        status: updatedCountDiff !== 0
          ? {
            ...state.filter.status,
            [RuleStatus.Updated]: { count: state.filter.status[RuleStatus.Updated].count + updatedCountDiff },
          }
          : state.filter.status,
      },
    };
  })
  .case(RulesManagementAction.applyValidation, (state, payload) => {
    const rules: IPermittedRuleRowModel[] = state.rules.map(({ errors, warnings, validity, ...rawRule }) => rawRule);
    const activeRules = rules.filter(rule => rule.status !== RuleStatus.Deleted);

    const validityFilter = { ...state.filter.validity };
    validityFilter[RuleValidity.Error].count = 0;
    validityFilter[RuleValidity.Unused].count = 0;
    validityFilter[RuleValidity.Useless].count = 0;

    payload?.forEach(({ index, errors, warnings }) => {
      const rule = activeRules[index];
      if (!rule) return;

      if (warnings) {
        rule.warnings = warnings;
        if (RuleWarningType.Unused in warnings) {
          rule.validity = RuleValidity.Unused;
          validityFilter[RuleValidity.Unused].count++;
        } else if (RuleWarningType.Useless in warnings) {
          rule.validity = RuleValidity.Useless;
          validityFilter[RuleValidity.Useless].count++;
        }
      }
      if (errors) {
        rule.errors = errors;
        rule.validity = RuleValidity.Error;
        validityFilter[RuleValidity.Error].count++;
      }
    });

    return {
      ...state,
      rules,
      filter: {
        ...state.filter,
        validity: validityFilter,
      },
    };
  })
  .case(RulesManagementAction.setHasErrors, (state, { id, invalid }) => {
    const invalidRules = new Set(state.invalidRules);
    if (invalid) {
      invalidRules.add(id);
    } else {
      invalidRules.delete(id);
    }

    return {
      ...state,
      invalidRules,
    };
  })
  .case(RulesManagementAction.init, (state, { settingsKey }) => {
    return {
      ...state,
      settingsKey,
    };
  })
  .case(RulesManagementAction.setSidebarOpen, (state, payload) => {
    return {
      ...state,
      isSidebarOpen: payload,
    };
  })
  .case(RulesManagementAction.setFilteredCount, (state, filteredCount) => {
    return {
      ...state,
      filteredCount,
    };
  })
  .case(RulesManagementAction.toggleFilterMode, (state) => {
    return {
      ...state,
      filterMode: state.filterMode === FilterMode.Strict ? FilterMode.Relaxed : FilterMode.Strict,
    };
  })
  .case(RulesManagementAction.destroy, (): IRulesManagementStore => {
    return structuredClone(rulesManagementInitialState);
  });
